/**
 * Einstellungen › Betrieb › Schließtage (Einheit E2a, Migration 748).
 *
 * Tage, an denen der Kalender der Anfrage-Widgets nicht auswählbar ist: einzelne Daten, jährlich
 * wiederkehrende Tage (24.12.) und bewegliche Feiertage (Ostersonntag). Wochentage ohne Öffnungszeit
 * laut Google-Profil sperrt MAESTRO selbst; sie stehen hier nur zur Information. Ein einzelner Tag kann
 * als „ausnahmsweise geöffnet“ eingetragen werden und hebt dann jede andere Sperre auf.
 */
import { useState } from "react";
import { Button, Card, Checkbox, DateInput, ErrorNote, Input, LoadingRows, Select, datumIsoZuDe } from "./components";
import { apiMutate, useApi } from "../hooks/useApi";

type Art = "datum" | "jaehrlich" | "regel";

export interface SchliesstagEintrag {
  id: string;
  art: Art;
  datum: string | null;
  monatTag: string | null;
  regel: string | null;
  offen: boolean;
  grund: string | null;
}

interface Antwort {
  canEdit: boolean;
  eintraege: SchliesstagEintrag[];
  ruhetage: string[];
}

export const REGEL_LABEL: Record<string, string> = {
  karfreitag: "Karfreitag",
  ostersonntag: "Ostersonntag",
  ostermontag: "Ostermontag",
  christi_himmelfahrt: "Christi Himmelfahrt",
  pfingstsonntag: "Pfingstsonntag",
  pfingstmontag: "Pfingstmontag",
  fronleichnam: "Fronleichnam",
};

const MONATE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

/** Zeilentext eines Eintrags, z. B. „Jedes Jahr am 24. Dezember“ oder „26.12.2026 – ausnahmsweise geöffnet“. */
export function eintragText(e: Pick<SchliesstagEintrag, "art" | "datum" | "monatTag" | "regel" | "offen">): string {
  if (e.art === "datum" && e.datum) return e.offen ? `${datumIsoZuDe(e.datum)} – ausnahmsweise geöffnet` : `${datumIsoZuDe(e.datum)} – geschlossen`;
  if (e.art === "jaehrlich" && e.monatTag) {
    const [m, t] = e.monatTag.split("-").map(Number);
    return `Jedes Jahr am ${t}. ${MONATE[(m as number) - 1] ?? ""}`;
  }
  return `Jedes Jahr: ${REGEL_LABEL[e.regel ?? ""] ?? e.regel ?? ""}`;
}

export function SchliesstageKarte() {
  const q = useApi<Antwort>("/api/settings/schliesstage");
  const [art, setArt] = useState<Art>("datum");
  const [datum, setDatum] = useState("");
  const [offen, setOffen] = useState(false);
  const [tag, setTag] = useState("24");
  const [monat, setMonat] = useState("12");
  const [regel, setRegel] = useState("ostersonntag");
  const [grund, setGrund] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const daten = q.data;

  async function anlegen() {
    setFehler(null);
    let body: Record<string, unknown>;
    if (art === "datum") {
      if (!datum) return setFehler("Bitte einen Tag wählen.");
      body = { art, datum, offen, ...(grund.trim() ? { grund: grund.trim() } : {}) };
    } else if (art === "jaehrlich") {
      body = { art, monatTag: `${monat.padStart(2, "0")}-${tag.padStart(2, "0")}`, ...(grund.trim() ? { grund: grund.trim() } : {}) };
    } else {
      body = { art, regel, ...(grund.trim() ? { grund: grund.trim() } : {}) };
    }
    setBusy(true);
    try {
      await apiMutate("/api/settings/schliesstage", "POST", body);
      setDatum("");
      setGrund("");
      setOffen(false);
      q.reload();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Das Speichern hat nicht geklappt.");
    } finally {
      setBusy(false);
    }
  }

  async function entfernen(id: string) {
    setFehler(null);
    setBusy(true);
    try {
      await apiMutate(`/api/settings/schliesstage/${id}`, "DELETE");
      q.reload();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Das Entfernen hat nicht geklappt.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-6 lg:col-span-3" data-testid="schliesstage-karte">
      <h3 className="mb-2 font-headline-sm text-headline-sm text-on-background dark:text-inverse-on-surface">Schließtage</h3>
      <p className="mb-4 font-body-md text-body-md text-on-surface-variant">
        An diesen Tagen kann im Kalender der Anfrage-Widgets nicht gefeiert werden. Wochentage ohne Öffnungszeit sperrt MAESTRO selbst.
      </p>
      {q.loading && !daten ? (
        <LoadingRows rows={2} />
      ) : q.error && !daten ? (
        <ErrorNote message={q.error} onRetry={q.reload} />
      ) : daten ? (
        <div className="space-y-4">
          {daten.ruhetage.length > 0 && (
            <p className="font-label-md text-label-md text-on-surface-variant" data-testid="schliesstage-ruhetage">
              Ruhetage laut Öffnungszeiten: {daten.ruhetage.join(", ")}
            </p>
          )}
          {daten.eintraege.length === 0 ? (
            <p className="font-body-md text-body-md text-on-surface-variant">Noch keine eigenen Schließtage eingetragen.</p>
          ) : (
            <ul className="divide-y divide-outline-variant/30">
              {daten.eintraege.map((e) => (
                <li key={e.id} className="flex items-center justify-between gap-3 py-2" data-testid="schliesstage-zeile">
                  <span className="font-body-md text-body-md">
                    {eintragText(e)}
                    {e.grund ? <span className="text-on-surface-variant"> · {e.grund}</span> : null}
                  </span>
                  {daten.canEdit && (
                    <Button variant="secondary" disabled={busy} onClick={() => void entfernen(e.id)} aria-label={`${eintragText(e)} entfernen`}>
                      Entfernen
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {daten.canEdit ? (
            <div className="space-y-3 border-t border-outline-variant/30 pt-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <Select
                  ariaLabel="Art des Schließtags"
                  value={art}
                  onValueChange={(v) => setArt(v as Art)}
                  options={[
                    { value: "datum", label: "Einzelner Tag" },
                    { value: "jaehrlich", label: "Jedes Jahr am …" },
                    { value: "regel", label: "Beweglicher Feiertag" },
                  ]}
                />
                {art === "datum" && <DateInput value={datum} onValueChange={setDatum} />}
                {art === "jaehrlich" && (
                  <div className="flex gap-2">
                    <Input inputMode="numeric" maxLength={2} aria-label="Tag" value={tag} onChange={(e) => setTag(e.target.value.replace(/\D/g, ""))} />
                    <Select ariaLabel="Monat" value={monat} onValueChange={setMonat} options={MONATE.map((n, i) => ({ value: String(i + 1), label: n }))} />
                  </div>
                )}
                {art === "regel" && (
                  <Select ariaLabel="Feiertag" value={regel} onValueChange={setRegel} options={Object.entries(REGEL_LABEL).map(([value, label]) => ({ value, label }))} />
                )}
                <Input maxLength={200} aria-label="Grund" placeholder="Grund, zum Beispiel Betriebsurlaub" value={grund} onChange={(e) => setGrund(e.target.value)} />
              </div>
              {art === "datum" && (
                <label className="flex items-center gap-2 font-body-md text-body-md">
                  <Checkbox checked={offen} onChange={(e) => setOffen(e.target.checked)} />
                  Ausnahmsweise geöffnet (hebt eine andere Sperre an diesem Tag auf)
                </label>
              )}
              {fehler && (
                <p role="alert" className="font-label-md text-label-md text-error">
                  {fehler}
                </p>
              )}
              <Button disabled={busy} onClick={() => void anlegen()}>
                Eintragen
              </Button>
            </div>
          ) : (
            <p className="font-label-md text-label-md text-on-surface-variant">Schließtage ändern nur Inhaber und Admins.</p>
          )}
        </div>
      ) : null}
    </Card>
  );
}
